import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Md5 } from 'ts-md5';

import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, first, map, pluck, switchMap } from 'rxjs/operators';

import { basePath } from '@atmi/core/injection';
import {
  ResponseSuccess,
  User,
  UserActions,
  UserLoginResponse,
  UserState,
  UserTypeActions,
} from '@atmi/core/models';

const initialUserState: UserState = {
  action: UserTypeActions.INIT,
  error: undefined,
  token: undefined,
  user: undefined,
};

const USER_KEY = 'atmi-user';

@Injectable({
  providedIn: 'root',
})
export class UserService {

  private readonly path = this.baseUrl + '/sessions';
  private readonly userPath = this.baseUrl + '/users';

  private readonly state$ = new BehaviorSubject<UserState>(initialUserState);

  constructor(
    private readonly http: HttpClient,
    @Inject(basePath) private readonly baseUrl: string,
  ) {
    const userStateFromStorage = localStorage.getItem(USER_KEY);

    if (userStateFromStorage) {
      const userState: UserState = JSON.parse(userStateFromStorage);
      this.setUserState(userState);
      // Mark for verification - will be checked after initialization
      this.needsVerification = true;
    }
  }

  dispatch(action: UserActions) {
    switch (action.type) {
      case UserTypeActions.LOGIN:
        const userLoginRequest = action.payload.user;

        this.http.post<ResponseSuccess<UserLoginResponse>>(this.path, { ...userLoginRequest })
          .pipe(
            switchMap((res) => {
              if (res.data) {
                this.setUserState({ token: res.data.token, error: undefined });
                return this.http.get<ResponseSuccess<User>>(`${this.userPath}/${res.data.user}`,
                  { headers: { Authorization: res.data.token } },
                );
              } else {
                return throwError((res as any).error);
              }
            }),
            first(),
            catchError((error) => {
              this.setUserState({ user: undefined, token: undefined, action: action.type, error: true });
              return throwError(error);
            }),
          )
          .subscribe((res) => {
            const info = res.data;
            this.setUserState({ user: info, action: action.type, error: undefined });
          });
        break;

      case UserTypeActions.LOGOUT:
        this.state$.next({ user: undefined, token: undefined, action: action.type, error: undefined });
        this.setUserState({ user: undefined, token: undefined, action: action.type, error: undefined });
        break;

      case UserTypeActions.UPDATE_INFO:
        const userEdit = action.payload.user;

        this.http.put<ResponseSuccess<User>>(`${this.userPath}/${userEdit.id}`, { ...userEdit })
          .pipe(
            first(),
            catchError((error) => {
              this.setUserState({ action: action.type, error: true });
              return throwError(error);
            }),
          )
          .subscribe((res) => {
            const user = res.data;
            this.setUserState({ user, action: action.type, error: undefined });
          });
        break;

      case UserTypeActions.CHANGE_PASSWORD:
        const userWithNoPasswordHashed = action.payload.user;
        const hashedPassword = Md5.hashStr(userWithNoPasswordHashed.password) as string;
        const userWithPasswordHashed = { ...userWithNoPasswordHashed, password: hashedPassword };

        this.http.put<ResponseSuccess<User>>(`${this.userPath}/${userWithPasswordHashed.id}`, { ...userWithPasswordHashed })
          .pipe(
            first(),
            catchError((error) => {
              this.setUserState({ action: action.type, error: true });
              return throwError(error);
            }),
          )
          .subscribe((res) => {
            const user = res.data;
            this.setUserState({ user, action: action.type, error: undefined });
          });
    }

  }

  selectState() {
    return this.state$.asObservable();
  }

  selectStateSnasphot() {
    return this.state$.value;
  }

  selectQueryState<T = UserState[keyof UserState]>(query: keyof UserState): Observable<T> {
    return this.state$.pipe(
      pluck(query) as any, // FIXME: Improve types
      distinctUntilChanged(), // FIXME: Improve what observe
    );
  }

  selectAuthenticatedState() {
    return this.state$.pipe(map((state) => !!state.token));
  }

  private setUserState(userState: Partial<UserState>) {
    const newUserState: UserState = { ...this.state$.value, ...userState };

    this.state$.next(newUserState);
    localStorage.setItem(USER_KEY, JSON.stringify(newUserState));
  }

  private needsVerification = false;

  async verifyStoredUser(): Promise<void> {
    if (!this.needsVerification) {
      return;
    }

    const currentState = this.state$.value;
    if (currentState.token && currentState.user?.id) {
      try {
        const isValid = await this.verifyUserOnServer(currentState.token, currentState.user.id);
        if (!isValid) {
          console.log('UserService: User verification failed, clearing stored authentication');
          this.clearStoredAuth();
        }
      } catch (error) {
        console.error('UserService: Error during user verification:', error);
        this.clearStoredAuth();
      }
    } else {
      this.clearStoredAuth();
    }

    this.needsVerification = false;
  }

  private async verifyUserOnServer(token: string, userId: string): Promise<boolean> {
    try {
      const response = await this.http.get<ResponseSuccess<User>>(`${this.userPath}/${userId}`, {
        headers: { Authorization: token }
      }).pipe(first()).toPromise();

      return !!response?.data;
    } catch (error) {
      console.error('UserService: User verification failed:', error);
      return false;
    }
  }

  private clearStoredAuth() {
    localStorage.removeItem(USER_KEY);
    this.state$.next(initialUserState);
  }

}
